// Study data: chapters read (from the iOS reader's record, synced through the API), private
// reflections, and the canon's chapter counts. Reading plans live in plans.ts.
//
// Reflections are private notes: they stay in this browser (localStorage) on the prototype and on the
// API alike, and are never sent anywhere. Eddy hasn't approved storing them on the server
// (09-BOOKS-AND-AUTHORS §4.3; BSLIB_BACKEND_PLAN keeps private notes on the device).
import { lookupPassage } from "./passages";

/** What a reflection was on: a passage of Scripture, the day's quote, or a section of a book. */
export type ReflectionSource =
  | { kind: "scripture" }
  | { kind: "quote"; text: string; author: string; work: string; libraryId: string; sectionUid: string | null }
  | { kind: "section"; libraryId: string; sectionUid: string; workTitle: string };

export type Reflection = {
  id: string;
  date: string;
  /** OSIS for Scripture; `q:<libraryId>` for a quote; `b:<libraryId>/<sectionUid>` for a section. */
  refKey: string;
  refLabel: string;
  /** Absent on reflections kept before books: those are on Scripture. */
  source?: ReflectionSource;
  answers: { prompt: string; text: string }[];
};

const KEY = "bs.reflections";

function readLocal(): Reflection[] {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Reflection[]) : [];
  } catch {
    return [];
  }
}

function writeLocal(items: Reflection[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(items.slice(0, 500)));
  } catch {
    // Private windows and full storage: the reflection is still shown for this visit.
  }
}

/** Reflections kept in this browser, newest first. `seed` (the prototype's example) is shown after them and never stored. */
export function localReflections(seed: Reflection[] = []): Pick<StudySource, "reflections" | "saveReflection"> {
  let session: Reflection[] | null = null;
  return {
    async reflections() {
      session ??= readLocal();
      return [...session, ...seed];
    },
    async saveReflection(r) {
      const entry: Reflection = { ...r, id: `rf-${crypto.randomUUID()}`, date: new Date().toISOString().slice(0, 10) };
      session = [entry, ...(session ?? readLocal())];
      writeLocal(session);
      return entry;
    },
  };
}

/** Reflections as a plain text file, newest first, for the reader to keep. */
export function reflectionsText(items: Reflection[]): string {
  const day = (iso: string) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  const blocks = items.map((r) => {
    const quote = r.source?.kind === "quote" ? `\n“${r.source.text}”` : "";
    const answers = r.answers.map((a) => `${a.prompt}\n${a.text}`).join("\n\n");
    return `${day(r.date)}\n${r.refLabel}${quote}\n\n${answers}`;
  });
  return `My reflections, kept in Fellowship\n\n${blocks.join("\n\n———\n\n")}\n`;
}

export type ChapterCounts = Record<string, number>;

/** Chapters per book (Protestant canon), for the map of the Bible read. */
export const CHAPTERS: [string, string, number][] = [
  ["Gen", "Genesis", 50], ["Exod", "Exodus", 40], ["Lev", "Leviticus", 27], ["Num", "Numbers", 36], ["Deut", "Deuteronomy", 34],
  ["Josh", "Joshua", 24], ["Judg", "Judges", 21], ["Ruth", "Ruth", 4], ["1Sam", "1 Samuel", 31], ["2Sam", "2 Samuel", 24],
  ["1Kgs", "1 Kings", 22], ["2Kgs", "2 Kings", 25], ["1Chr", "1 Chronicles", 29], ["2Chr", "2 Chronicles", 36], ["Ezra", "Ezra", 10],
  ["Neh", "Nehemiah", 13], ["Esth", "Esther", 10], ["Job", "Job", 42], ["Ps", "Psalms", 150], ["Prov", "Proverbs", 31],
  ["Eccl", "Ecclesiastes", 12], ["Song", "Song of Songs", 8], ["Isa", "Isaiah", 66], ["Jer", "Jeremiah", 52], ["Lam", "Lamentations", 5],
  ["Ezek", "Ezekiel", 48], ["Dan", "Daniel", 12], ["Hos", "Hosea", 14], ["Joel", "Joel", 3], ["Amos", "Amos", 9],
  ["Obad", "Obadiah", 1], ["Jonah", "Jonah", 4], ["Mic", "Micah", 7], ["Nah", "Nahum", 3], ["Hab", "Habakkuk", 3],
  ["Zeph", "Zephaniah", 3], ["Hag", "Haggai", 2], ["Zech", "Zechariah", 14], ["Mal", "Malachi", 4],
  ["Matt", "Matthew", 28], ["Mark", "Mark", 16], ["Luke", "Luke", 24], ["John", "John", 21], ["Acts", "Acts", 28],
  ["Rom", "Romans", 16], ["1Cor", "1 Corinthians", 16], ["2Cor", "2 Corinthians", 13], ["Gal", "Galatians", 6], ["Eph", "Ephesians", 6],
  ["Phil", "Philippians", 4], ["Col", "Colossians", 4], ["1Thess", "1 Thessalonians", 5], ["2Thess", "2 Thessalonians", 3], ["1Tim", "1 Timothy", 6],
  ["2Tim", "2 Timothy", 4], ["Titus", "Titus", 3], ["Phlm", "Philemon", 1], ["Heb", "Hebrews", 13], ["Jas", "James", 5],
  ["1Pet", "1 Peter", 5], ["2Pet", "2 Peter", 3], ["1John", "1 John", 5], ["2John", "2 John", 1], ["3John", "3 John", 1],
  ["Jude", "Jude", 1], ["Rev", "Revelation", 22],
];

export const REFLECT_PROMPTS = [
  { key: "notice", prompt: "What stands out to you?", hint: "A word, a phrase, a turn in the argument." },
  { key: "god", prompt: "What does it show you of God?", hint: "His character, his promises, his works." },
  { key: "respond", prompt: "What will you do with it today?", hint: "One small, real thing." },
  { key: "pray", prompt: "Pray it back.", hint: "In your own words. No one else sees this unless you share it." },
];

export interface StudySource {
  chaptersRead(): Promise<Record<string, number[]>>;
  reflections(): Promise<Reflection[]>;
  saveReflection(r: Omit<Reflection, "id" | "date">): Promise<Reflection>;
}

const wait = <T>(v: T, ms = 80) => new Promise<T>((r) => setTimeout(() => r(structuredClone(v)), ms));

export function createMockStudy(): StudySource {
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const daysAgo = (n: number) => iso(new Date(Date.now() - n * 86_400_000));
  // Chapters read, as the iOS reader would report them.
  const chapters: Record<string, number[]> = {
    Gen: Array.from({ length: 50 }, (_, i) => i + 1),
    Exod: Array.from({ length: 40 }, (_, i) => i + 1),
    Ruth: [1, 2, 3, 4],
    Ps: Array.from({ length: 96 }, (_, i) => i + 1),
    Prov: Array.from({ length: 12 }, (_, i) => i + 1),
    Isa: Array.from({ length: 40 }, (_, i) => i + 1),
    Lam: [1, 2, 3],
    Jonah: [1, 2, 3, 4],
    Matt: Array.from({ length: 28 }, (_, i) => i + 1),
    Mark: Array.from({ length: 16 }, (_, i) => i + 1),
    John: Array.from({ length: 21 }, (_, i) => i + 1),
    Acts: Array.from({ length: 9 }, (_, i) => i + 1),
    Rom: Array.from({ length: 8 }, (_, i) => i + 1),
    Eph: Array.from({ length: 6 }, (_, i) => i + 1),
    Phil: Array.from({ length: 4 }, (_, i) => i + 1),
    Heb: [1, 2, 11, 12],
    Jas: [1],
    "1John": [1, 2, 3, 4, 5],
  };
  const p46 = lookupPassage("Psalm 46:1")!;
  const reflections: Reflection[] = [
    {
      id: "rf-1",
      date: daysAgo(6),
      refKey: p46.key,
      refLabel: p46.label,
      answers: [
        { prompt: REFLECT_PROMPTS[0]!.prompt, text: "“A very present help.” Not a help that arrives late." },
        { prompt: REFLECT_PROMPTS[3]!.prompt, text: "Lord, be present in the meeting at ten. Keep me from rushing ahead of you." },
      ],
    },
  ];

  const local = localReflections(reflections);
  return {
    chaptersRead: () => wait(chapters),
    reflections: async () => wait(await local.reflections()),
    saveReflection: async (r) => wait(await local.saveReflection(r)),
  };
}
